/** Parse stored Zulip auth into an `Authorization` header value. */
export const toBasicHeader = (zulipAuth: unknown): string | null => {
  if (!zulipAuth) {
    return null;
  }

  const normalize = (value: string) => {
    if (!value) {
      return null;
    }
    return value.startsWith("Basic ") ? value : `Basic ${value}`;
  };

  if (typeof zulipAuth === "string") {
    return normalize(zulipAuth.trim());
  }

  if (typeof zulipAuth === "object") {
    const auth = zulipAuth as Record<string, unknown>;
    const nestedAuth =
      (auth.data as Record<string, unknown> | undefined) ??
      (auth.result as Record<string, unknown> | undefined) ??
      (auth.payload as Record<string, unknown> | undefined);

    const directCandidates = [
      auth.authorization,
      auth.auth_header,
      auth.basic_auth,
      auth.basic_token,
      auth["basic token"],
      auth.token,
      auth.api_token,
      nestedAuth?.authorization,
      nestedAuth?.auth_header,
      nestedAuth?.basic_auth,
      nestedAuth?.basic_token,
      nestedAuth?.["basic token"],
      nestedAuth?.token,
      nestedAuth?.api_token,
    ];

    for (const candidate of directCandidates) {
      if (typeof candidate === "string" && candidate.trim()) {
        return normalize(candidate.trim());
      }
    }

    const emailCandidates = [auth.email, auth.zulip_email];
    const apiKeyCandidates = [auth.api_key, auth.zulip_api_key];
    const email = emailCandidates.find(
      value => typeof value === "string" && value.trim()
    ) as string | undefined;
    const apiKey = apiKeyCandidates.find(
      value => typeof value === "string" && value.trim()
    ) as string | undefined;

    if (email && apiKey) {
      const encoded = window.btoa(`${email}:${apiKey}`);
      return `Basic ${encoded}`;
    }
  }

  return null;
};

export const toZulipUserId = (zulipAuth: unknown): number | null => {
  if (!zulipAuth || typeof zulipAuth !== "object") {
    return null;
  }
  const auth = zulipAuth as Record<string, unknown>;
  const nestedAuth =
    (auth.data as Record<string, unknown> | undefined) ??
    (auth.result as Record<string, unknown> | undefined) ??
    (auth.payload as Record<string, unknown> | undefined);

  const candidates = [
    auth.zulip_user_id,
    auth.user_id,
    nestedAuth?.zulip_user_id,
  ];
  for (const candidate of candidates) {
    if (typeof candidate === "number") {
      return candidate;
    }
    if (typeof candidate === "string" && candidate.trim()) {
      const parsed = Number(candidate);
      if (!Number.isNaN(parsed)) {
        return parsed;
      }
    }
  }
  return null;
};
